import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import type { UseQueryResult } from '@tanstack/react-query/src/types';
import type { Request } from 'express';
import { sfccGet } from '../../utils/sitegenesis';
import { isServer } from '../../utils/utils';
import { bffGet } from '../bff';
import type { SearchResponse } from './model/bloomreach';

const BR_COOKIE_NAME_IDENTITY = '_br_uid_2';
const SEARCH_TERM_MIN_LENGTH = 2;

enum QueryKey {
    BLOOMREACH_SEARCH = 'br-search',
    BRAND_SUGGEST = 'brand-suggest',
}

export type HttpParams = Record<string, string | number | boolean>[];
export type BrandSuggestions = {
    available: boolean;
    brands: string[];
};

const sortAndFlattenParams = (params: HttpParams): Array<string | number | boolean> => {
    const flattenedParams: Array<string | number | boolean> = [];
    params.sort().forEach((paramObject) => {
        Object.entries(paramObject)
            .sort()
            .forEach((entry) => {
                flattenedParams.push(...entry);
            });
    });

    return flattenedParams;
};

export const shouldQueryBloomreach = (searchTerm: string): boolean =>
    Boolean(searchTerm && searchTerm.length >= SEARCH_TERM_MIN_LENGTH);

const defaultBrUID2Cookie = (): string => `uid=${uuidv4()}:v=app:ts=0:hc=1`;
export const getBrUID2Cookie = (): string => {
    if (isServer) {
        return defaultBrUID2Cookie();
    }
    const brUID2 = Cookies.get(BR_COOKIE_NAME_IDENTITY) as string;
    if (!brUID2) {
        const newBrUID2 = defaultBrUID2Cookie();
        Cookies.set(BR_COOKIE_NAME_IDENTITY, newBrUID2, { secure: true });
        return newBrUID2;
    }
    return brUID2;
};

export const getProductSearchHeaders = (req?: Partial<Request>): Record<string, string> => {
    if (isServer) {
        return {
            'x-slt-brUID2': getBrUID2Cookie(),
            'x-slt-ref-url': req && req.get ? `${req.protocol}://${req.get('host')}` : '',
            'x-slt-url':
                req && req.get ? `${req.protocol}://${req.get('host')}${req.originalUrl}` : '',
        };
    }
    return {
        'x-slt-brUID2': getBrUID2Cookie(),
        'x-slt-ref-url': window.document.referrer,
        'x-slt-url': window.location.href,
    };
};

export const useGetBloomreachSearch = ({
    q,
    pageSize,
    url,
    refUrl,
}: {
    q: string;
    pageSize: number;
    url: string;
    refUrl: string;
}): UseQueryResult<SearchResponse> => {
    const brUID2 = getBrUID2Cookie();
    const httpParams: HttpParams = [{ brUID2 }, { q }, { pageSize }];
    httpParams.push({ refUrl });
    httpParams.push({ url });
    const flatSortedUrlParamsArray = sortAndFlattenParams(httpParams);

    return useQuery<SearchResponse>({
        keepPreviousData: true,
        staleTime: 1000,
        queryFn: async () => bffGet<SearchResponse>('bloomreach/search', { httpParams }),
        queryKey: [QueryKey.BLOOMREACH_SEARCH, ...flatSortedUrlParamsArray],
        enabled: shouldQueryBloomreach(q),
    });
};

export const useGetSuggestedBrands = ({
    searchTerm,
}: {
    searchTerm: string;
}): UseQueryResult<BrandSuggestions> =>
    useQuery<BrandSuggestions>({
        queryFn: async ({ signal }) =>
            sfccGet<BrandSuggestions>('PwaSearch-GetSuggestedBrands', [{ searchTerm }], signal),
        queryKey: [QueryKey.BRAND_SUGGEST, searchTerm],
        enabled: shouldQueryBloomreach(searchTerm),
    });
